// ============================================================
// invoiceNumber.ts — the firm's bill-numbering template (Invoicing Stage 0).
// Tokens: {YYYY} {YY} {MM} {SEQ} {SEQ:n} (n = zero-pad width 1-8).
// {SEQ} is REQUIRED (it's what makes a number unique). Literal characters
// are limited to letters, digits and - / _ . so numbers stay file/URL safe.
// FRONTEND TWIN of backend/src/lib/invoiceNumber.ts (keep in sync) — used for
// the Admin → Billing live preview only; the server is
// authoritative (validation + numbering when a bill is ISSUED).
// ============================================================
export const DEFAULT_INVOICE_NUMBER_FORMAT = 'INV-{YYYY}-{SEQ:4}'
export const DEFAULT_CREDIT_NOTE_NUMBER_FORMAT = 'CN-{YYYY}-{SEQ:4}'
const TOKEN = /\{(YYYY|YY|MM|SEQ(?::([1-8]))?)\}/g

export const validateInvoiceNumberFormat = (fmt: string): string | null => {
  if (fmt.length === 0 || fmt.length > 40) return 'Use 1–40 characters.'
  if (!/\{SEQ(?::[1-8])?\}/.test(fmt)) return 'Include {SEQ} (the running number) so every bill number is unique.'
  const literal = fmt.replace(TOKEN, '')
  if (/[{}]/.test(literal)) return 'Unknown placeholder — use {YYYY}, {YY}, {MM} or {SEQ}.'
  if (!/^[A-Za-z0-9\-/_.]*$/.test(literal)) return 'Only letters, numbers and - / _ . are allowed around the placeholders.'
  return null
}

// ── Plain-English builder (Admin → Billing) ──────────────────
// The builder never shows tokens. It edits these parts and assembles the SAME
// stored format string the server validates and numbers from:
//   [prefix]-[{YYYY}|{YY}]-[{MM}]-{SEQ[:n]}   (empty parts dropped, joined by "-")
// The running number is always included, last.
export interface NumberFormatParts {
  prefix: string            // "" = no prefix
  year: 'none' | 'YYYY' | 'YY'
  month: boolean
  pad: number               // 0 = no padding ({SEQ}); 1–8 = {SEQ:n}
}
export const PREFIX_MAX = 12
export const prefixError = (p: string): string | null => {
  if (p.length > PREFIX_MAX) return `Keep the prefix to ${PREFIX_MAX} characters or fewer.`
  if (!/^[A-Za-z0-9]*$/.test(p)) return 'Use letters and numbers only in the prefix — no spaces or symbols (the dashes are added for you).'
  return null
}
export const padError = (n: number): string | null => (Number.isInteger(n) && n >= 0 && n <= 8 ? null : 'Choose a number length from 1 to 8 digits.')
export const buildNumberFormat = (p: NumberFormatParts): string =>
  [p.prefix, p.year === 'none' ? '' : `{${p.year}}`, p.month ? '{MM}' : '', p.pad ? `{SEQ:${p.pad}}` : '{SEQ}'].filter(Boolean).join('-')
/** Stored format → builder parts, or null when it can't be represented (then the UI offers the raw "advanced" field). */
export const parseNumberFormat = (fmt: string): NumberFormatParts | null => {
  const m = /^(?:([A-Za-z0-9]{1,12})-)?(?:\{(YYYY|YY)\}-)?(?:(\{MM\})-)?\{SEQ(?::([1-8]))?\}$/.exec(fmt.trim())
  if (!m) return null
  const parts: NumberFormatParts = { prefix: m[1] ?? '', year: (m[2] as 'YYYY' | 'YY' | undefined) ?? 'none', month: !!m[3], pad: m[4] ? Number(m[4]) : 0 }
  return buildNumberFormat(parts) === fmt.trim() ? parts : null
}

export const formatInvoiceNumber = (fmt: string, seq: number, date: Date = new Date()): string =>
  fmt.replace(TOKEN, (_m, tok: string, pad?: string) => {
    if (tok === 'YYYY') return String(date.getUTCFullYear())
    if (tok === 'YY') return String(date.getUTCFullYear()).slice(-2)
    if (tok === 'MM') return String(date.getUTCMonth() + 1).padStart(2, '0')
    return pad ? String(seq).padStart(Number(pad), '0') : String(seq)
  })
